import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import SuccessionThemeProvider, { useSuccessionTheme } from './SuccessionThemeProvider';
import { validUkPostcode } from '../../lib/ukFormatters';
import RjVisibilityFirstRunModal from './RjVisibility/RjVisibilityFirstRunModal';
import { 
  EnvelopeIcon, 
  LockClosedIcon, 
  UserIcon,
  GlobeAltIcon,
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

// GraphQL Mutation for updating user settings
// Note: Arguments use snake_case because they're defined with camelize: false in the mutation
const UPDATE_CURRENT_USER = gql`
  mutation UpdateCurrentUser(
    $name: String
    $locale: String
    $postal_code: String
    $birth_date: String
    $agency_name: String
    $show_city: Boolean
    $accepts_system_email: Boolean
    $send_daily_update: Boolean
    $send_user_assignment_update: Boolean
    $send_task_assignment_update: Boolean
    $send_case_assignment_update: Boolean
    $send_new_users_update: Boolean
  ) {
    updateCurrentUser(
      name: $name
      locale: $locale
      postal_code: $postal_code
      birth_date: $birth_date
      agency_name: $agency_name
      show_city: $show_city
      accepts_system_email: $accepts_system_email
      send_daily_update: $send_daily_update
      send_user_assignment_update: $send_user_assignment_update
      send_task_assignment_update: $send_task_assignment_update
      send_case_assignment_update: $send_case_assignment_update
      send_new_users_update: $send_new_users_update
    )
  }
`;

// RJ Seller Visibility Workflow (rj_seller_visibility, id 344) — self-service opt-in.
// See docs/plans/2026-07-17-001-feat-rj-seller-visibility-workflow-plan.md
const RJ_TOGGLE_VISIBILITY = gql`
  mutation RjToggleVisibility($enabled: Boolean!) {
    rj_toggle_visibility(enabled: $enabled) {
      success
      errors
      marketplace_visibility_opt_in
    }
  }
`;

// Verbatim copy from Ashley (Raymond James) — do NOT paraphrase. Reversibility
// sentence appended per Ashley/Rob's 2026-07-20 review: legal sign-off waived
// as long as we surface that the seller can turn visibility off at any time.
const RJ_VISIBILITY_OPT_IN_COPY =
  "You are now selecting to be visible to buyers. This will mean that buyers will be able to view your profile on the platform via the search feature, you could be contacted by buyers wanting to connect, and there could be instances where a buyer recognizes you by your location and reaches out to you outside of the platform. You can turn this off at any time from your settings.";
const RJ_VISIBILITY_OPT_OFF_COPY =
  "You are now selecting to turn off visibility, which will mean that your profile will go back to hidden and is not searchable.";

// Alert Component
const Alert = ({ type, message, onClose }) => {
  const icons = {
    success: <CheckCircleIcon className="h-5 w-5" />,
    error: <XCircleIcon className="h-5 w-5" />,
    warning: <ExclamationTriangleIcon className="h-5 w-5" />,
    info: <InformationCircleIcon className="h-5 w-5" />
  };

  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  return (
    <div className={`flex items-center p-4 mb-4 border rounded-lg ${colors[type]}`}>
      <div className="flex-shrink-0">{icons[type]}</div>
      <div className="ml-3 flex-1">
        <p className="text-sm font-medium">{message}</p>
      </div>
      {onClose && (
        <button onClick={onClose} className="ml-3 flex-shrink-0 text-current opacity-50 hover:opacity-100">
          <XCircleIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

const SuccessionSettingsContent = ({ current_user, company, features }) => {
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const intl = useIntl();

  // Ensure we have valid data with defaults
  const user = current_user || {};
  const companyData = company || {};
  const featureFlags = features || [];

  // GraphQL mutation hook
  const [updateCurrentUser, { loading: updating }] = useMutation(UPDATE_CURRENT_USER);
  const [rjToggleVisibility, { loading: togglingVisibility }] = useMutation(RJ_TOGGLE_VISIBILITY);

  // RJ Seller Visibility Workflow — local state mirrors user.marketplace_visibility_opt_in.
  const [marketplaceVisibilityOptIn, setMarketplaceVisibilityOptIn] = useState(
    !!user?.marketplace_visibility_opt_in
  );
  const [visibilityDialogIntent, setVisibilityDialogIntent] = useState(null); // 'enable' | 'disable' | null

  // State for form fields
  const [formData, setFormData] = useState({
    locale: user?.locale || 'en',
    accepts_system_email: user?.accepts_system_email || false,
    send_daily_update: user?.send_daily_update || false,
    send_user_assignment_update: user?.send_user_assignment_update || false,
    send_task_assignment_update: user?.send_task_assignment_update || false,
    send_case_assignment_update: user?.send_case_assignment_update || false,
    send_new_users_update: user?.send_new_users_update || false,
    show_city: user?.show_city || false
  });

  // State for email change
  const [emailData, setEmailData] = useState({
    email: '',
    password: ''
  });

  // State for password change
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  // State for contact info
  const [contactData, setContactData] = useState({
    name: user?.name || '',
    postal_code: user?.postal_code || '',
    birth_date: user?.birth_date || '',
    agency_name: user?.agency_name || ''
  });

  // State for modals
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  // State for password visibility
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // State for alerts
  const [alert, setAlert] = useState({
    show: false,
    type: 'success',
    message: ''
  });

  // Check if feature is enabled
  const isFeatureEnabled = (featureName) => {
    return featureFlags && featureFlags.includes(featureName);
  };

  // Helper function to get CSRF token (still needed for password/email REST endpoints)
  const getCsrfToken = () => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // RJ Seller Visibility Workflow — open the confirmation modal on toggle click.
  // The actual mutation fires only after the user confirms in the modal.
  const handleVisibilityToggleClick = () => {
    setVisibilityDialogIntent(marketplaceVisibilityOptIn ? 'disable' : 'enable');
  };

  const handleVisibilityConfirm = async () => {
    const nextValue = visibilityDialogIntent === 'enable';
    try {
      const result = await rjToggleVisibility({ variables: { enabled: nextValue } });
      const payload = result?.data?.rj_toggle_visibility;
      if (payload?.success) {
        setMarketplaceVisibilityOptIn(payload.marketplace_visibility_opt_in);
        if (typeof window !== 'undefined' && window.ahoy) {
          window.ahoy.track(nextValue ? 'rj_visibility_opt_in' : 'rj_visibility_opt_out');
        }
        setAlert({
          show: true,
          type: 'success',
          message: nextValue
            ? intl.formatMessage({ id: 'settings.succession.rj_visibility.enabled_success', defaultMessage: 'You are now visible to buyers.' })
            : intl.formatMessage({ id: 'settings.succession.rj_visibility.disabled_success', defaultMessage: 'Your profile is now hidden from buyer search.' }),
        });
      } else {
        setAlert({ show: true, type: 'error', message: (payload?.errors || []).join(', ') || 'Update failed' });
      }
    } catch (err) {
      console.error('[RJ Visibility] toggle failed:', err);
      setAlert({ show: true, type: 'error', message: 'Update failed. Please try again.' });
    } finally {
      setVisibilityDialogIntent(null);
    }
  };

  // Handle settings form submission using GraphQL
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Form submitted with data:', formData);
    
    try {
      // Use GraphQL mutation - automatically uses current_user from context
      const result = await updateCurrentUser({
        variables: {
          locale: formData.locale,
          accepts_system_email: formData.accepts_system_email,
          send_daily_update: formData.send_daily_update,
          send_user_assignment_update: formData.send_user_assignment_update,
          send_task_assignment_update: formData.send_task_assignment_update,
          send_case_assignment_update: formData.send_case_assignment_update,
          send_new_users_update: formData.send_new_users_update,
          show_city: formData.show_city
        }
      });

      console.log('GraphQL mutation result:', result);

      if (result.data?.updateCurrentUser) {
        setAlert({ show: true, type: 'success', message: intl.formatMessage({ id: 'settings.succession.alerts.settings_updated' }) });
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setAlert({ show: true, type: 'error', message: intl.formatMessage({ id: 'settings.succession.alerts.settings_error' }) });
      }
    } catch (error) {
      console.error('Settings update error:', error);
      setAlert({ show: true, type: 'error', message: error.message || 'An error occurred while updating settings' });
    }
    
    return false;
  };

  // Handle email change
  const handleEmailChange = async () => {
    if (!emailData.email || !emailData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setAlert({ show: true, type: 'error', message: intl.formatMessage({ id: 'settings.succession.alerts.validation.email_required' }) });
      return;
    }

    if (isFeatureEnabled('identity_authentication') && !emailData.password) {
      setAlert({ show: true, type: 'error', message: intl.formatMessage({ id: 'settings.succession.alerts.validation.password_required' }) });
      return;
    }

    try {
      const response = await fetch('/account/email', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({ user: emailData })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAlert({ show: true, type: 'success', message: data.message || intl.formatMessage({ id: 'settings.succession.alerts.email_updated' }) });
        setEmailDialogOpen(false);
        setEmailData({ email: '', password: '' });
        setTimeout(() => window.location.href = '/login', 2000);
      } else {
        setAlert({ show: true, type: 'error', message: data.error || intl.formatMessage({ id: 'settings.succession.alerts.email_error' }) });
      }
    } catch (error) {
      console.error('Email update error:', error);
      setAlert({ show: true, type: 'error', message: 'An error occurred while updating email' });
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      setAlert({ show: true, type: 'error', message: intl.formatMessage({ id: 'settings.succession.alerts.validation.all_password_required' }) });
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setAlert({ show: true, type: 'error', message: intl.formatMessage({ id: 'settings.succession.alerts.validation.passwords_mismatch' }) });
      return;
    }

    if (passwordData.new_password.length < 6) {
      setAlert({ show: true, type: 'error', message: intl.formatMessage({ id: 'settings.succession.alerts.validation.password_length' }) });
      return;
    }

    try {
      const response = await fetch('/account/password', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({ user: passwordData })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAlert({ show: true, type: 'success', message: data.message || intl.formatMessage({ id: 'settings.succession.alerts.password_updated' }) });
        setPasswordDialogOpen(false);
        setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      } else {
        setAlert({ show: true, type: 'error', message: data.error || intl.formatMessage({ id: 'settings.succession.alerts.password_error' }) });
      }
    } catch (error) {
      console.error('Password update error:', error);
      setAlert({ show: true, type: 'error', message: 'An error occurred while updating password' });
    }
  };

  const isGbTenant = companyData?.country === 'gb';
  const postalCodeError = (() => {
    if (!isGbTenant) return null;
    const value = (contactData.postal_code || '').trim();
    if (!value) return null;
    if (validUkPostcode(value)) return null;
    return intl.formatMessage({
      id: 'settings.succession.membership.invalid_uk_postcode',
      defaultMessage: 'Please enter a valid UK postcode (e.g. SW1A 1AA)',
    });
  })();

  // Handle contact info change using GraphQL
  const handleContactChange = async () => {
    if (postalCodeError) {
      setAlert({ show: true, type: 'error', message: postalCodeError });
      return;
    }

    try {
      // Use GraphQL mutation - automatically uses current_user from context
      const result = await updateCurrentUser({
        variables: {
          name: contactData.name,
          postal_code: contactData.postal_code,
          birth_date: contactData.birth_date,
          agency_name: contactData.agency_name
        }
      });

      if (result.data?.updateCurrentUser) {
        setAlert({ show: true, type: 'success', message: intl.formatMessage({ id: 'settings.succession.alerts.contact_updated' }) });
        setContactDialogOpen(false);
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setAlert({ show: true, type: 'error', message: intl.formatMessage({ id: 'settings.succession.alerts.contact_error' }) });
      }
    } catch (error) {
      console.error('Contact info update error:', error);
      setAlert({ show: true, type: 'error', message: error.message || 'An error occurred while updating contact information' });
    }
  };

  return (
    <div className="flex h-screen bg-succession-bg-secondary transition-colors duration-300">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-succession-bg-primary border-b border-succession-border shadow-sm transition-colors duration-300">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-succession-text-primary transition-colors duration-300">
                  <FormattedMessage id="settings.succession.title" />
                </h1>
                <p className="text-sm text-succession-text-secondary transition-colors duration-300">
                  <FormattedMessage id="settings.succession.subtitle" />
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto px-6 py-8">
            {alert.show && (
              <Alert 
                type={alert.type} 
                message={alert.message} 
                onClose={() => setAlert({ show: false, type: 'success', message: '' })} 
              />
            )}

            {/* Membership Section */}
            <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md mb-6 transition-colors duration-300">
              <div className="px-6 py-5 border-b border-succession-border">
                <h2 className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                  <FormattedMessage id="settings.succession.membership.title" />
                </h2>
                <p className="text-sm text-succession-text-secondary mt-1 transition-colors duration-300">
                  <FormattedMessage id="settings.succession.membership.subtitle" />
                </p>
              </div>
              <div className="p-6 space-y-4">
                {isFeatureEnabled('identity_authentication') && (
                  <>
                    <div className="flex items-center justify-between py-3 border-b border-succession-border">
                      <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-5 w-5 text-succession-text-secondary" />
                        <div>
                          <p className="text-sm font-medium text-succession-text-primary">
                            <FormattedMessage id="settings.succession.membership.email" />
                          </p>
                          <p className="text-sm text-succession-text-secondary">{user.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setEmailDialogOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-succession-primary hover:text-succession-primary-dark border border-succession-primary hover:bg-succession-primary-10 rounded-lg transition-colors"
                      >
                        <FormattedMessage id="settings.succession.membership.change_email" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-succession-border">
                      <div className="flex items-center space-x-3">
                        <LockClosedIcon className="h-5 w-5 text-succession-text-secondary" />
                        <div>
                          <p className="text-sm font-medium text-succession-text-primary">
                            <FormattedMessage id="settings.succession.membership.password" />
                          </p>
                          <p className="text-sm text-succession-text-secondary">••••••••</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setPasswordDialogOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-succession-primary hover:text-succession-primary-dark border border-succession-primary hover:bg-succession-primary-10 rounded-lg transition-colors"
                      >
                        <FormattedMessage id="settings.succession.membership.change_password" />
                      </button>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between py-3 border-b border-succession-border">
                  <div className="flex items-center space-x-3">
                    <UserIcon className="h-5 w-5 text-succession-text-secondary" />
                    <div>
                      <p className="text-sm font-medium text-succession-text-primary">
                        <FormattedMessage id="settings.succession.membership.name" />
                      </p>
                      <p className="text-sm text-succession-text-secondary">{user.name}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setContactDialogOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-succession-primary hover:text-succession-primary-dark border border-succession-primary hover:bg-succession-primary-10 rounded-lg transition-colors"
                  >
                    <FormattedMessage id="settings.succession.membership.edit_contact_info" />
                  </button>
                </div>

                {isFeatureEnabled('identity_authentication') && user.postal_code && (
                  <div className="py-3 border-b border-succession-border">
                    <p className="text-sm font-medium text-succession-text-primary">
                      <FormattedMessage 
                        id={`settings.succession.membership.${companyData.country === 'us' ? 'zip_code' : 'postal_code'}`}
                      />
                    </p>
                    <p className="text-sm text-succession-text-secondary mt-1">{user.postal_code}</p>
                  </div>
                )}

                {companyData.business_type !== 'wealth_rj' && user.birth_date && (
                  <div className="py-3 border-b border-succession-border">
                    <p className="text-sm font-medium text-succession-text-primary">
                      <FormattedMessage id="settings.succession.membership.birth_date" />
                    </p>
                    <p className="text-sm text-succession-text-secondary mt-1">{user.birth_date}</p>
                  </div>
                )}

                {isFeatureEnabled('agency_name') && user.agency_name && (
                  <div className="py-3">
                    <p className="text-sm font-medium text-succession-text-primary">
                      <FormattedMessage id="settings.succession.membership.agency_name" />
                    </p>
                    <p className="text-sm text-succession-text-secondary mt-1">{user.agency_name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Settings Section */}
            <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md transition-colors duration-300">
              <div className="px-6 py-5 border-b border-succession-border">
                <h2 className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                  <FormattedMessage id="settings.succession.preferences.title" />
                </h2>
                <p className="text-sm text-succession-text-secondary mt-1 transition-colors duration-300">
                  <FormattedMessage id="settings.succession.preferences.subtitle" />
                </p>
              </div>
              <form onSubmit={handleSettingsSubmit} className="p-6 space-y-6">
                {/* RJ Seller Visibility Workflow — surfaced only when flag ON + succession user */}
                {isFeatureEnabled('rj_seller_visibility') && user.motivation === 'succession' && (
                  <div className="pb-6 border-b border-succession-border">
                    <div className="flex items-start justify-between space-x-4">
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-succession-text-primary">
                          <FormattedMessage
                            id="settings.succession.rj_visibility.title"
                            defaultMessage="Marketplace visibility"
                          />
                        </h3>
                        <p className="text-sm text-succession-text-secondary mt-1">
                          <FormattedMessage
                            id="settings.succession.rj_visibility.subtitle"
                            defaultMessage="Control whether buyers can find and message you through the marketplace."
                          />
                        </p>
                        {!user.minimum_profile_completed && !marketplaceVisibilityOptIn && (
                          <p className="text-xs text-yellow-700 mt-2">
                            <FormattedMessage
                              id="settings.succession.rj_visibility.profile_incomplete"
                              defaultMessage="Complete your profile to appear in search."
                            />
                          </p>
                        )}
                      </div>
                      {/* State-driven toggle — avoids Tailwind peer-checked/after pseudo-class JIT quirks
                          that leave the knob invisible on some builds. Track and knob positions
                          are conditional on state; role=switch keeps it accessible. */}
                      <label className="inline-flex items-center cursor-pointer flex-shrink-0 select-none">
                        <input
                          type="checkbox"
                          checked={marketplaceVisibilityOptIn}
                          onChange={handleVisibilityToggleClick}
                          disabled={togglingVisibility}
                          className="sr-only"
                          role="switch"
                          aria-checked={marketplaceVisibilityOptIn}
                        />
                        <span
                          className={
                            'relative inline-block w-11 h-6 rounded-full transition-colors ' +
                            (marketplaceVisibilityOptIn ? 'bg-succession-primary' : 'bg-gray-300')
                          }
                          aria-hidden="true"
                        >
                          <span
                            className={
                              'absolute top-0.5 h-5 w-5 bg-white border border-gray-300 rounded-full shadow transition-transform ' +
                              (marketplaceVisibilityOptIn ? 'translate-x-[22px]' : 'translate-x-0.5')
                            }
                          />
                        </span>
                        <span className="ml-3 text-sm text-succession-text-primary">
                          {marketplaceVisibilityOptIn ? (
                            <FormattedMessage id="settings.succession.rj_visibility.on" defaultMessage="Visible" />
                          ) : (
                            <FormattedMessage id="settings.succession.rj_visibility.off" defaultMessage="Hidden" />
                          )}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {companyData.country === 'us' && isFeatureEnabled('show_county') && (
                  <div className="pb-6 border-b border-succession-border">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_city}
                        onChange={(e) => handleFormChange('show_city', e.target.checked)}
                        className="w-4 h-4 text-succession-primary border-gray-300 rounded focus:ring-succession-primary"
                      />
                      <span className="text-sm text-succession-text-primary">
                        <FormattedMessage id="settings.succession.preferences.show_city" />
                      </span>
                    </label>
                  </div>
                )}

                {!(companyData.business_type === 'wealth_rj' && companyData.country === 'us') && (
                  <div className="pb-6 border-b border-succession-border">
                    <div className="flex items-center space-x-3 mb-3">
                      <GlobeAltIcon className="h-5 w-5 text-succession-text-secondary" />
                      <h3 className="text-base font-semibold text-succession-text-primary">
                        <FormattedMessage id="settings.succession.preferences.language" />
                      </h3>
                    </div>
                    <select
                      value={formData.locale}
                      onChange={(e) => handleFormChange('locale', e.target.value)}
                      className="block w-full max-w-xs px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent transition-colors"
                    >
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                      <option value="en-GB">English (UK)</option>
                    </select>
                  </div>
                )}

                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    <BellIcon className="h-5 w-5 text-succession-text-secondary" />
                    <h3 className="text-base font-semibold text-succession-text-primary">
                      <FormattedMessage id="settings.succession.preferences.notifications.title" />
                    </h3>
                  </div>
                  <p className="text-sm text-succession-text-secondary mb-4">
                    <FormattedMessage id="settings.succession.preferences.notifications.subtitle" />
                  </p>

                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.accepts_system_email}
                        onChange={(e) => handleFormChange('accepts_system_email', e.target.checked)}
                        className="w-4 h-4 text-succession-primary border-gray-300 rounded focus:ring-succession-primary"
                      />
                      <span className="text-sm text-succession-text-primary">
                        <FormattedMessage id="settings.succession.preferences.notifications.system" />
                      </span>
                    </label>

                    {(user.customer || user.staff) && (
                      <>
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.send_daily_update}
                            onChange={(e) => handleFormChange('send_daily_update', e.target.checked)}
                            className="w-4 h-4 text-succession-primary border-gray-300 rounded focus:ring-succession-primary"
                          />
                          <span className="text-sm text-succession-text-primary">
                            <FormattedMessage id="settings.succession.preferences.notifications.daily" />
                          </span>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.send_user_assignment_update}
                            onChange={(e) => handleFormChange('send_user_assignment_update', e.target.checked)}
                            className="w-4 h-4 text-succession-primary border-gray-300 rounded focus:ring-succession-primary"
                          />
                          <span className="text-sm text-succession-text-primary">
                            <FormattedMessage id="settings.succession.preferences.notifications.user_assignment" />
                          </span>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.send_task_assignment_update}
                            onChange={(e) => handleFormChange('send_task_assignment_update', e.target.checked)}
                            className="w-4 h-4 text-succession-primary border-gray-300 rounded focus:ring-succession-primary"
                          />
                          <span className="text-sm text-succession-text-primary">
                            <FormattedMessage id="settings.succession.preferences.notifications.task_assignment" />
                          </span>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.send_case_assignment_update}
                            onChange={(e) => handleFormChange('send_case_assignment_update', e.target.checked)}
                            className="w-4 h-4 text-succession-primary border-gray-300 rounded focus:ring-succession-primary"
                          />
                          <span className="text-sm text-succession-text-primary">
                            <FormattedMessage id="settings.succession.preferences.notifications.case_assignment" />
                          </span>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.send_new_users_update}
                            onChange={(e) => handleFormChange('send_new_users_update', e.target.checked)}
                            className="w-4 h-4 text-succession-primary border-gray-300 rounded focus:ring-succession-primary"
                          />
                          <span className="text-sm text-succession-text-primary">
                            <FormattedMessage id="settings.succession.preferences.notifications.new_users" />
                          </span>
                        </label>
                      </>
                    )}
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="px-6 py-3 bg-succession-primary hover:bg-succession-primary-dark text-white font-medium rounded-lg transition-colors shadow-sm hover:shadow-md"
                  >
                    <FormattedMessage id="settings.succession.buttons.save_preferences" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Email Change Modal */}
      {emailDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-succession-bg-primary border border-succession-border rounded-lg max-w-md w-full shadow-xl transition-colors">
            <div className="px-6 py-4 border-b border-succession-border">
              <h3 className="text-lg font-semibold text-succession-text-primary">
                <FormattedMessage id="settings.succession.modals.email.title" />
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-succession-text-secondary">
                <FormattedMessage id="settings.succession.modals.email.subtitle" />
              </p>
              
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  <FormattedMessage id="settings.succession.modals.email.current_email" />
                </label>
                <input
                  type="text"
                  value={user.email}
                  disabled
                  className="w-full px-3 py-2 border border-succession-border rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  <FormattedMessage id="settings.succession.modals.email.new_email" />
                </label>
                <input
                  type="email"
                  value={emailData.email || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEmailData(prev => ({ ...prev, email: value }));
                  }}
                  placeholder="john.doe@example.com"
                  className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent"
                />
              </div>

              {isFeatureEnabled('identity_authentication') && (
                <div>
                  <label className="block text-sm font-medium text-succession-text-primary mb-2">
                    <FormattedMessage id="settings.succession.modals.email.current_password" />
                  </label>
                  <input
                    type="password"
                    value={emailData.password || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEmailData(prev => ({ ...prev, password: value }));
                    }}
                    className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent"
                  />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-succession-border flex justify-end space-x-3">
              <button
                onClick={() => setEmailDialogOpen(false)}
                className="px-4 py-2 text-sm font-medium text-succession-text-secondary hover:text-succession-text-primary border border-succession-border rounded-lg hover:bg-succession-bg-secondary transition-colors"
              >
                <FormattedMessage id="settings.succession.buttons.cancel" />
              </button>
              <button
                onClick={handleEmailChange}
                className="px-4 py-2 text-sm font-medium bg-succession-primary hover:bg-succession-primary-dark text-white rounded-lg transition-colors"
              >
                <FormattedMessage id="settings.succession.buttons.save_changes" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {passwordDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-succession-bg-primary border border-succession-border rounded-lg max-w-md w-full shadow-xl transition-colors">
            <div className="px-6 py-4 border-b border-succession-border">
              <h3 className="text-lg font-semibold text-succession-text-primary">
                <FormattedMessage id="settings.succession.modals.password.title" />
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  <FormattedMessage id="settings.succession.modals.password.current_password" />
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? 'text' : 'password'}
                    value={passwordData.old_password || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPasswordData(prev => ({ ...prev, old_password: value }));
                    }}
                    className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-succession-text-secondary hover:text-succession-text-primary"
                  >
                    {showOldPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  <FormattedMessage id="settings.succession.modals.password.new_password" />
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPasswordData(prev => ({ ...prev, new_password: value }));
                    }}
                    className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-succession-text-secondary hover:text-succession-text-primary"
                  >
                    {showNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-succession-text-secondary mt-1">
                  <FormattedMessage id="settings.succession.modals.password.min_chars" />
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  <FormattedMessage id="settings.succession.modals.password.confirm_password" />
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirm_password || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPasswordData(prev => ({ ...prev, confirm_password: value }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent pr-10 ${
                      passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password
                        ? 'border-red-500'
                        : 'border-succession-border'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-succession-text-secondary hover:text-succession-text-primary"
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                  <p className="text-xs text-red-500 mt-1">
                    <FormattedMessage id="settings.succession.modals.password.passwords_mismatch" />
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-succession-border flex justify-end space-x-3">
              <button
                onClick={() => setPasswordDialogOpen(false)}
                className="px-4 py-2 text-sm font-medium text-succession-text-secondary hover:text-succession-text-primary border border-succession-border rounded-lg hover:bg-succession-bg-secondary transition-colors"
              >
                <FormattedMessage id="settings.succession.buttons.cancel" />
              </button>
              <button
                onClick={handlePasswordChange}
                disabled={!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password || passwordData.new_password !== passwordData.confirm_password}
                className="px-4 py-2 text-sm font-medium bg-succession-primary hover:bg-succession-primary-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FormattedMessage id="settings.succession.buttons.save_changes" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Info Change Modal */}
      {contactDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-succession-bg-primary border border-succession-border rounded-lg max-w-md w-full shadow-xl transition-colors">
            <div className="px-6 py-4 border-b border-succession-border">
              <h3 className="text-lg font-semibold text-succession-text-primary">
                <FormattedMessage id="settings.succession.modals.contact.title" />
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-succession-text-secondary">
                <FormattedMessage id="settings.succession.modals.contact.subtitle" />
              </p>
              
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  <FormattedMessage id="settings.succession.membership.name" />
                </label>
                <input
                  type="text"
                  value={contactData.name || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setContactData(prev => ({ ...prev, name: value }));
                  }}
                  className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent"
                />
              </div>

              {isFeatureEnabled('identity_authentication') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-succession-text-primary mb-2">
                      <FormattedMessage 
                        id={`settings.succession.membership.${companyData.country === 'us' ? 'zip_code' : 'postal_code'}`}
                      />
                    </label>
                    <input
                      type="text"
                      value={contactData.postal_code || ''}
                      placeholder={isGbTenant ? 'e.g. SW1A 1AA' : undefined}
                      onChange={(e) => {
                        const value = e.target.value;
                        setContactData(prev => ({ ...prev, postal_code: value }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent ${postalCodeError ? 'border-red-500' : 'border-succession-border'}`}
                    />
                    {postalCodeError && (
                      <p className="text-sm text-red-600 mt-1">{postalCodeError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-succession-text-primary mb-2">
                      <FormattedMessage id="settings.succession.membership.birth_date" />
                    </label>
                    <input
                      type="date"
                      value={contactData.birth_date || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setContactData(prev => ({ ...prev, birth_date: value }));
                      }}
                      className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent"
                    />
                  </div>

                  {isFeatureEnabled('agency_name') && (
                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-2">
                        <FormattedMessage id="settings.succession.membership.agency_name" />
                      </label>
                      <input
                        type="text"
                        value={contactData.agency_name || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setContactData(prev => ({ ...prev, agency_name: value }));
                        }}
                        className="w-full px-3 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-2 focus:ring-succession-primary focus:border-transparent"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-succession-border flex justify-end space-x-3">
              <button
                onClick={() => setContactDialogOpen(false)}
                className="px-4 py-2 text-sm font-medium text-succession-text-secondary hover:text-succession-text-primary border border-succession-border rounded-lg hover:bg-succession-bg-secondary transition-colors"
              >
                <FormattedMessage id="settings.succession.buttons.cancel" />
              </button>
              <button
                onClick={handleContactChange}
                disabled={!!postalCodeError}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${postalCodeError ? 'bg-succession-text-muted cursor-not-allowed' : 'bg-succession-primary hover:bg-succession-primary-dark'}`}
              >
                <FormattedMessage id="settings.succession.buttons.save_changes" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RJ Seller Visibility Workflow — one-shot intro modal for succession users
          on their first visit after the tenant enables the flag. Idempotent via
          localStorage; caller passes a compound `enabled` gate. */}
      <RjVisibilityFirstRunModal
        userId={user?.id}
        enabled={isFeatureEnabled('rj_seller_visibility') && user?.motivation === 'succession'}
      />

      {/* RJ Seller Visibility Workflow — verbatim confirmation modal. Ashley's copy. */}
      {visibilityDialogIntent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-succession-bg-primary border border-succession-border rounded-lg max-w-md w-full shadow-xl transition-colors">
            <div className="px-6 py-4 border-b border-succession-border">
              <h3 className="text-lg font-semibold text-succession-text-primary">
                {visibilityDialogIntent === 'enable' ? (
                  <FormattedMessage
                    id="settings.succession.rj_visibility.modal_title_enable"
                    defaultMessage="Turn marketplace visibility on?"
                  />
                ) : (
                  <FormattedMessage
                    id="settings.succession.rj_visibility.modal_title_disable"
                    defaultMessage="Turn marketplace visibility off?"
                  />
                )}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-succession-text-primary leading-relaxed">
                {visibilityDialogIntent === 'enable' ? RJ_VISIBILITY_OPT_IN_COPY : RJ_VISIBILITY_OPT_OFF_COPY}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-succession-border flex justify-end space-x-3">
              <button
                onClick={() => setVisibilityDialogIntent(null)}
                disabled={togglingVisibility}
                className="px-4 py-2 text-sm font-medium text-succession-text-secondary hover:text-succession-text-primary border border-succession-border rounded-lg hover:bg-succession-bg-secondary transition-colors"
              >
                <FormattedMessage id="settings.succession.buttons.cancel" defaultMessage="Cancel" />
              </button>
              <button
                onClick={handleVisibilityConfirm}
                disabled={togglingVisibility}
                className="px-4 py-2 text-sm font-medium bg-succession-primary hover:bg-succession-primary-dark text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {visibilityDialogIntent === 'enable' ? (
                  <FormattedMessage id="settings.succession.rj_visibility.confirm_enable" defaultMessage="Yes, make me visible" />
                ) : (
                  <FormattedMessage id="settings.succession.rj_visibility.confirm_disable" defaultMessage="Yes, hide me" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main component wrapper with theme provider
const SuccessionSettings = ({ current_user, company, features, theme_name, messages_count }) => {
  return (
    <SuccessionThemeProvider company={company} user={current_user}>
      <SuccessionSettingsContent 
        current_user={current_user} 
        company={company} 
        features={features}
      />
    </SuccessionThemeProvider>
  );
};

SuccessionSettings.propTypes = {
  current_user: PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
    name: PropTypes.string,
    locale: PropTypes.string,
    postal_code: PropTypes.string,
    birth_date: PropTypes.string,
    agency_name: PropTypes.string,
    accepts_system_email: PropTypes.bool,
    send_daily_update: PropTypes.bool,
    send_user_assignment_update: PropTypes.bool,
    send_task_assignment_update: PropTypes.bool,
    send_case_assignment_update: PropTypes.bool,
    send_new_users_update: PropTypes.bool,
    show_city: PropTypes.bool,
    customer: PropTypes.bool,
    staff: PropTypes.bool
  }).isRequired,
  company: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    country: PropTypes.string,
    business_type: PropTypes.string,
    primary_color: PropTypes.string,
    secondary_color: PropTypes.string
  }).isRequired,
  features: PropTypes.arrayOf(PropTypes.string),
  theme_name: PropTypes.string,
  messages_count: PropTypes.number
};

SuccessionSettingsContent.propTypes = {
  current_user: PropTypes.object.isRequired,
  company: PropTypes.object.isRequired,
  features: PropTypes.array,
  intl: PropTypes.object.isRequired
};

Alert.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func
};

export default SuccessionSettings;
